import { Button } from "@/components/ui/button";
import { ChartBar, FileText, MessagesSquare, BarChart3, Brain, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";

const ImpactReportsPage = () => {
  const navigate = useNavigate();

  return (
    <>
      <SEO 
        title="AI-Powered Impact Reports & Storytelling for Nonprofits" 
        description="Turn data into compelling stories that drive support and funding with our AI-powered impact reporting tools."
      />
      <div className="min-h-screen bg-gradient-to-b from-[#F2FCE2] to-white">
        {/* Hero Section */}
        <div className="relative overflow-hidden bg-[#2D7D46] text-white py-20">
          <div className="absolute inset-0 bg-[url('/images/hero-pattern.svg')] opacity-10" />
          <div className="container mx-auto px-4 relative">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Impact Reporting & Storytelling
              </h1>
              <p className="text-xl md:text-2xl text-gray-100 mb-1">
                Turn Data into Powerful Stories That Win Support & Funding.
              </p>
            </div>
          </div>
        </div>

        {/* Challenges Section */}
        <div className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                The Challenges Nonprofits Face with Impact Reporting
              </h2>
              <div className="grid md:grid-cols-3 gap-8 mb-12">
                <div className="bg-gray-50 rounded-xl p-6">
                  <div className="text-[#2D7D46] mb-4">
                    <FileText className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Time-Consuming Reports</h3>
                  <p className="text-gray-600">
                    Creating comprehensive, data-driven impact reports takes weeks of manual work.
                  </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-6">
                  <div className="text-[#2D7D46] mb-4">
                    <MessagesSquare className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Inconsistent Storytelling</h3>
                  <p className="text-gray-600">
                    Many organizations fail to translate numbers into compelling narratives that resonate.
                  </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-6">
                  <div className="text-[#2D7D46] mb-4">
                    <BarChart3 className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Donor Engagement</h3>
                  <p className="text-gray-600">
                    Reports often get buried in emails instead of driving engagement and donations.
                  </p>
                </div>
              </div>
              <div className="text-center bg-[#2D7D46]/5 rounded-xl p-8">
                <p className="text-lg text-gray-700 italic">
                  "Funders and donors engage 3x more with visually compelling impact updates than with traditional text-heavy reports."
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Solutions Section */}
        <div className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              AI-Powered Impact Reporting Tools
            </h2>
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="p-3 rounded-lg bg-[#2D7D46]/10 text-[#2D7D46] w-fit mb-4">
                  <ChartBar className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold mb-4">Impact Report Generator</h3>
                <p className="text-gray-600 mb-4">
                  AI automatically structures key metrics, success stories, and donor acknowledgments into professional reports.
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D7D46]" />
                    <span>Multiple Formats</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D7D46]" />
                    <span>Highlight metrics</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D7D46]" />
                    <span>Donor Branding</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="p-3 rounded-lg bg-[#2D7D46]/10 text-[#2D7D46] w-fit mb-4">
                  <Brain className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold mb-4">Impact Story Posts</h3>
                <p className="text-gray-600 mb-8">
                  Convert data into engaging storytelling with AI-crafted social media and email content.
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D7D46]" />
                    <span>Social media posts</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D7D46]" />
                    <span>Email campaigns</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D7D46]" />
                    <span>Visual storytelling</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="p-3 rounded-lg bg-[#2D7D46]/10 text-[#2D7D46] w-fit mb-4">
                  <FileText className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold mb-4">Post-Event Analytics</h3>
                <p className="text-gray-600 mb-6">
                  Transform event feedback into actionable insights with AI-powered surveys and analysis.
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D7D46]" />
                    <span>Automate surveys</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D7D46]" />
                    <span>Thank you email</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D7D46]" />
                    <span>Volunteer Attendance</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Impact Stats Section */}
        <div className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              The Impact of AI on Nonprofit Reporting
            </h2>
            <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
              <div className="text-center">
                <div className="text-4xl font-bold text-[#2D7D46] mb-2">70%</div>
                <p className="text-gray-600">Faster report creation</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-[#2D7D46] mb-2">3x</div>
                <p className="text-gray-600">Higher engagement</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-[#2D7D46] mb-2">50%</div>
                <p className="text-gray-600">Better feedback rates</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-[#2D7D46] mb-2">40%</div>
                <p className="text-gray-600">More donor retention</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-20 bg-[#2D7D46] text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-8">
              Get Started with Smart Impact Reporting
            </h2>
            <p className="text-xl mb-12 max-w-2xl mx-auto">
              Join thousands of nonprofits using AI to create compelling impact reports that drive support and funding.
            </p>
            <div className="space-y-4">
              <Button 
                size="lg"
                className="bg-white text-[#2D7D46] hover:bg-gray-100 text-lg px-8 py-6 h-auto w-full md:w-auto"
                onClick={() => navigate("/signup")}
              >
                <ChartBar className="w-5 h-5 mr-2" />
                Create Your First Impact Report
              </Button>
              <div className="mt-4">
                <Link 
                  to="/npo/tools" 
                  className="text-white hover:underline inline-flex items-center"
                >
                  Explore More Impact Tools <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ImpactReportsPage;
